import React, { useEffect, useRef } from 'react'
import Modal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.css'
import { useFormik } from 'formik'
import { Input, Checkbox, Select } from '../../components/ModalInputs'
import * as Yup from 'yup'
import ModalFooter from '../../components/ModalFooter/ModalFooter'
import moment from 'moment'
import { createTerm, updateTerm } from '../../actions/terms'
import { getSchoolYears, getTermGradeTypes, getTermTypes } from '../../actions/common'

const validationSchema = Yup.object({
  title: Yup.string().required('обязательное поле').max(50, 'Должно быть не более 50 символов'),
  term_begin: Yup.date().required('обязательное поле'),
  term_end: Yup.date().required('обязательное поле').min(Yup.ref('term_begin'), 'дата окончания не может быть раньше даты начала'),
  year: Yup.string().required('обязательное поле'),
  term_type_id: Yup.string().required('обязательное поле'),
})

export default function ModalComponent({ data, closeModal, height, setSearch }) {

  const dispatch = useDispatch()
  const { schoolYears, termTypes, termGradeTypes } = useSelector(state => state.common)

  // react-responsive-modal продолжает рендерить контент во время анимации закрытия (300ms),
  // а `data` уже становится null - держим последние данные в ref и обновляем синхронно во время
  // рендера (не в эффекте), иначе при переключении edit -> add форма на один рендер показывает
  // данные предыдущего элемента, и это попадает в initialValues формика
  const formDataRef = useRef(data)
  if (data) formDataRef.current = data
  const formData = formDataRef.current

  const addMode = !formData?.trmid

  useEffect(() => {
    !schoolYears && dispatch(getSchoolYears())
    !termTypes && dispatch(getTermTypes())
    !termGradeTypes && dispatch(getTermGradeTypes("?exclude_year=true"))
  }, [])

  const formatPayloadDates = (payload) => ({
    ...payload,
    term_begin: payload.term_begin ? moment(payload.term_begin).format('DD.MM.YYYY') : payload.term_begin,
    term_end: payload.term_end ? moment(payload.term_end).format('DD.MM.YYYY') : payload.term_end,
  })

  const handleCreate = async (values) => {
    const success = await dispatch(createTerm(formatPayloadDates(values)))
    if (success) {
      setSearch({ year: values.year })
      closeModal()
    }
  }

  const handleUpdate = async (values) => {
    const { trmid, ...payload } = values
    const success = await dispatch(updateTerm(trmid, formatPayloadDates(payload)))
    if (success) {
      setSearch({ year: values.year })
      closeModal()
    }
  }

  const {
    values,
    errors,
    touched,
    submitCount,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: addMode ? {} : formData,
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: addMode ? handleCreate : handleUpdate
  })

  const error = (fieldName) => (touched[fieldName] || submitCount) && errors[fieldName]

  const renderOptions = ({ name, xp_key, term_type_id, term_type_name }) => <option value={term_type_id || xp_key}>{term_type_name || name}</option>

  const isGradeTypeChecked = (trmtid) => {
    return !!values.term_grade_types?.includes(trmtid)
  }

  const handleGradeTypeChange = (trmtid) => (e) => {
    const current = values.term_grade_types || ''
    const ids = current ? current.split(',').filter(Boolean) : []

    if (e.target.checked) {
      ids.push(trmtid)
    } else {
      const index = ids.indexOf(String(trmtid))
      if (index > -1) ids.splice(index, 1)
    }

    setFieldValue('term_grade_types', ids.join(','))
  }

  return (
    <Modal
      open={!!data}
      onClose={() => false}
      showCloseIcon={false}
      height={height}
      classNames={{
        modal: styles.custom
      }}
    >
      <>
        <div className={styles.modalLocal}>
          <div className={styles.tabWrapper}>
            <div className={styles.inputsRow}>
              <Input
                style={{ width: '100%' }}
                label='Название'
                name='title'
                value={values.title || ''}
                error={error('title')}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.inputsRow}>
              <Select
                style={{ flex: 1 }}
                label='Учебный год'
                name='year'
                value={values.year || ''}
                error={error('year')}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value=''>Выбрать</option>
                {schoolYears?.map(renderOptions)}
              </Select>
              <Select
                style={{ flex: 1 }}
                label='Тип периода'
                name='term_type_id'
                value={values.term_type_id || ''}
                error={error('term_type_id')}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value=''>Выбрать</option>
                {termTypes?.map(renderOptions)}
              </Select>
            </div>
            <div className={styles.inputsRow}>
              <Input
                style={{ flex: 1 }}
                label='Дата начала'
                name='term_begin'
                type='date'
                value={values.term_begin ? moment(values.term_begin).format('YYYY-MM-DD') : ''}
                error={error('term_begin')}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Input
                style={{ flex: 1 }}
                label='Дата окончания'
                name='term_end'
                type='date'
                value={values.term_end ? moment(values.term_end).format('YYYY-MM-DD') : ''}
                error={error('term_end')}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxGroupLabel}>Тип оценок</label>
              <div className={styles.checkboxList}>
                {termGradeTypes?.map(({ title, trmtid }) => (
                  <Checkbox
                    key={trmtid}
                    label={title}
                    checked={isGradeTypeChecked(trmtid)}
                    onChange={handleGradeTypeChange(trmtid)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <ModalFooter handleSubmit={handleSubmit} closeModal={closeModal} />
      </>
    </Modal>
  )
}
